<script setup>
import { computed } from 'vue';
import { useLocale } from 'shared/composables/useLocale';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { resolvedLocale } = useLocale();

const formatDate = dateString => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(resolvedLocale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const formatCurrency = (amount, currency) => {
  const numericAmount = Number(amount) || 0;
  return new Intl.NumberFormat(resolvedLocale.value, {
    style: 'currency',
    currency: currency || 'ARS',
  }).format(numericAmount);
};

const getPaymentStatusClass = status => {
  const classes = {
    paid: 'bg-n-teal-5 text-n-teal-12',
  };

  return classes[status] || 'bg-n-solid-3 text-n-slate-12';
};

const orderStatus = computed(() => {
  const { status } = props.order;
  if (!status) return '';

  switch (status) {
    case 'open':
      return t('CONVERSATION_SIDEBAR.TIENDA_NUBE.ORDER_STATUS.OPEN');
    case 'closed':
      return t('CONVERSATION_SIDEBAR.TIENDA_NUBE.ORDER_STATUS.CLOSED');
    case 'cancelled':
      return t('CONVERSATION_SIDEBAR.TIENDA_NUBE.ORDER_STATUS.CANCELLED');
    default:
      return status;
  }
});

const paymentStatus = computed(() => {
  const status = props.order.payment_status;
  if (!status) return '';

  switch (status) {
    case 'pending':
      return t('CONVERSATION_SIDEBAR.TIENDA_NUBE.PAYMENT_STATUS.PENDING');
    case 'authorized':
      return t('CONVERSATION_SIDEBAR.TIENDA_NUBE.PAYMENT_STATUS.AUTHORIZED');
    case 'paid':
      return t('CONVERSATION_SIDEBAR.TIENDA_NUBE.PAYMENT_STATUS.PAID');
    case 'voided':
      return t('CONVERSATION_SIDEBAR.TIENDA_NUBE.PAYMENT_STATUS.VOIDED');
    case 'refunded':
      return t('CONVERSATION_SIDEBAR.TIENDA_NUBE.PAYMENT_STATUS.REFUNDED');
    default:
      return status;
  }
});
</script>

<template>
  <div
    class="py-3 border-b border-n-weak last:border-b-0 flex flex-col gap-1.5"
  >
    <div class="flex justify-between items-center gap-2">
      <div class="font-medium flex min-w-0">
        <a
          v-if="order.admin_url"
          :href="order.admin_url"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:underline text-n-slate-12 cursor-pointer truncate"
        >
          {{
            $t('CONVERSATION_SIDEBAR.TIENDA_NUBE.ORDER_ID', {
              id: order.number || order.id,
            })
          }}
          <i class="i-lucide-external-link pl-1" />
        </a>
        <span v-else class="truncate text-n-slate-12">
          {{
            $t('CONVERSATION_SIDEBAR.TIENDA_NUBE.ORDER_ID', {
              id: order.number || order.id,
            })
          }}
        </span>
      </div>
      <div
        :class="getPaymentStatusClass(order.payment_status)"
        class="text-xs px-2 py-1 rounded capitalize truncate"
        :title="paymentStatus"
      >
        {{ paymentStatus }}
      </div>
    </div>
    <div class="text-sm text-n-slate-12">
      <span
        v-if="formatDate(order.created_at)"
        class="text-n-slate-11 border-r border-n-weak pr-2"
      >
        {{ formatDate(order.created_at) }}
      </span>
      <span class="text-n-slate-11 pl-2">
        {{ formatCurrency(order.total, order.currency) }}
      </span>
    </div>
    <div v-if="orderStatus">
      <span class="capitalize font-medium text-n-slate-11" :title="orderStatus">
        {{ orderStatus }}
      </span>
    </div>
  </div>
</template>
